import { Dialog, DialogConfig, DialogRef } from '@angular/cdk/dialog';
import { Injectable, Type } from '@angular/core';

import { ModalComponent, ModalConfig } from './modal.component';

interface ModalProps<T> {
  data?: T;
  options?: Pick<
    DialogConfig<T>,
    | 'ariaDescribedBy'
    | 'ariaLabel'
    | 'ariaLabelledBy'
    | 'ariaModal'
    | 'autoFocus'
    | 'direction'
  > &
    ModalConfig & {
      disableBackdropClose?: boolean;
    };
}

@Injectable({
  providedIn: 'root'
})
export class ModalService {
  private _refs: DialogRef<unknown>[] = [];

  constructor(private dialog: Dialog) {}

  open<T, C = unknown>(component: Type<C>, props: ModalProps<T> = {}) {
    const { data, options } = props || {};
    const {
      title,
      description,
      disableBackdropClose,
      closeButton,
      enableFullscreenOnMobile,
      backgroundColor,
      size,
      rounded,
      ...defaultOptions
    } = options || {};

    const shouldShowCloseButton = () => {
      if (typeof closeButton === 'boolean') {
        return closeButton ?? false;
      }

      return {
        showOnMobile: closeButton?.showOnMobile ?? false,
        showOnDesktop: closeButton?.showOnDesktop ?? false
      };
    };
    const isFullscreenOnMobile = enableFullscreenOnMobile ?? false;

    const ref = this.dialog.open(ModalComponent, {
      ...defaultOptions,
      disableClose: disableBackdropClose,
      maxWidth: '100vw',
      data: {
        component,
        options: {
          title,
          description,
          closeButton: shouldShowCloseButton(),
          enableFullscreenOnMobile: isFullscreenOnMobile,
          backgroundColor: backgroundColor ?? '',
          size: size ?? 'md',
          rounded: rounded ?? 'md'
        },
        ...data
      }
    });
    this._refs.push(ref);
    return ref;
  }

  close<T = unknown>(result?: T): void {
    const ref = this._refs.pop();
    if (ref) {
      ref.close(result);
    }
  }

  closeAll(result?: unknown): void {
    this._refs.forEach((ref) => ref.close(result));
    this._refs = [];
  }
}
